// src/components/dashboard/SoilSensorPanel.js
// Shows colocated soil sensor data inside WeatherStationDashboard
import React, { useState, useEffect } from 'react';
import { fetchSoilForStation, SOIL_LABELS } from '../../services/soilService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmt = (v, dec = 1) => (v != null && !isNaN(v) ? Number(v).toFixed(dec) : '—');

const SoilMetricCard = ({ field, value }) => {
  const meta = SOIL_LABELS[field] || { label: field, unit: '', color: '#6b7280' };
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: meta.color }}>
        {meta.label}
      </div>
      <div className="text-xl font-black text-gray-900">
        {fmt(value, field === 'ph' ? 1 : field === 'ec' ? 0 : 1)}
        {meta.unit && <span className="text-sm font-medium text-gray-500 ml-1">{meta.unit}</span>}
      </div>
    </div>
  );
};

const SoilSensorPanel = ({ weatherStationId }) => {
  const [readings, setReadings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [chart,    setChart]    = useState('hum'); // active chart variable

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const data = await fetchSoilForStation(weatherStationId, { limit: 48 });
      if (active) {
        setReadings(data || []);
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => { active = false; clearInterval(interval); };
  }, [weatherStationId]);

  if (loading) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 animate-pulse">
        <div className="h-4 bg-amber-200 rounded w-40 mb-3" />
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-amber-100 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!readings.length) return null;

  const latest = readings[0]?.soilData || {};
  const primaryFields = ['hum', 'temp', 'ph', 'ec'];
  const nutrientFields = ['nitro', 'phos', 'pota'];

  // Chart data (chronological)
  const chartData = [...readings].reverse().map(r => ({
    time: new Date(r.receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    value: r.soilData?.[chart],
  }));

  const chartMeta = SOIL_LABELS[chart] || {};

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-amber-900 text-sm uppercase tracking-wide">Soil Sensor — 10 cm</h3>
          <p className="text-xs text-amber-600 mt-0.5">
            Last update: {readings[0]?.receivedAt
              ? new Date(readings[0].receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : '—'}
          </p>
        </div>
        <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center">
          <span className="text-white text-base">🌱</span>
        </div>
      </div>

      {/* Primary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {primaryFields.map(f => <SoilMetricCard key={f} field={f} value={latest[f]} />)}
      </div>

      {/* Nutrients */}
      <div className="grid grid-cols-3 gap-3">
        {nutrientFields.map(f => <SoilMetricCard key={f} field={f} value={latest[f]} />)}
      </div>

      {/* Trend chart */}
      <div className="bg-white rounded-xl p-4 border border-amber-100">
        {/* Variable selector */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Object.entries(SOIL_LABELS).filter(([f]) => f !== 'battery_mv').map(([f, m]) => (
            <button
              key={f}
              onClick={() => setChart(f)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                chart === f ? 'text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={chart === f ? { backgroundColor: m.color } : {}}
            >
              {m.label}
            </button>
          ))}
        </div>

        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip
              formatter={(v) => [v != null ? `${Number(v).toFixed(1)}${chartMeta.unit || ''}` : '—', chartMeta.label]}
              labelStyle={{ fontSize: 11, fontWeight: 600 }}
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 11 }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartMeta.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Battery */}
      {latest.battery_mv != null && (
        <div className="flex items-center gap-2 text-xs text-amber-700">
          <span>Battery: {latest.battery_mv} mV</span>
          <div className="flex-1 h-1.5 bg-amber-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-green-500"
              style={{ width: `${Math.min(100, ((latest.battery_mv - 2000) / 2200) * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SoilSensorPanel;
